/**
 * German interface strings — the source of truth.
 *
 * `en.ts` is declared as `Dictionary`, so anything added here without an
 * English counterpart fails the typecheck. Legal citations stay in German in
 * both dictionaries: § 49 Abs. 4 PBefG has no English equivalent, and a
 * translated citation cannot be looked up.
 */

export const de = {
  common: {
    loading: 'Wird geladen …',
    save: 'Speichern',
    saving: 'Speichert …',
    cancel: 'Abbrechen',
    back: 'Zurück',
    remove: 'Entfernen',
    from: 'Von',
    to: 'Bis',
    status: 'Status',
    search: 'Suche',
    all: 'Alle',
    dash: '—',
    saveFailed: 'Speichern fehlgeschlagen.',
    unknown: 'Unbekannt',
  },

  nav: {
    overview: 'Übersicht',
    trips: 'Fahrten',
    orders: 'Aufträge',
    shifts: 'Schichten',
    invoices: 'Rechnungen',
    export: 'Export',
    backup: 'Sicherung',
    settings: 'Einstellungen',
    more: 'Mehr',
    lock: 'Sperren',
    closeMenu: 'Menü schließen',
  },

  shell: {
    backupDueTitle: 'Datensicherung fällig',
    backupNow: 'Jetzt sichern',
    companyMissingTitle: 'Firmendaten fehlen',
    companyMissingBody:
      'Ohne Firmenname, Anschrift und Steuernummer lassen sich keine Rechnungen nach § 14 Abs. 4 UStG erstellen.',
    companyMissingLink: 'Jetzt eintragen',
    vehicleMissingTitle: 'Kein Fahrzeug angelegt',
    vehicleMissingBody: 'Fahrten und Schichten brauchen ein Fahrzeug mit Kennzeichen.',
    vehicleMissingLink: 'Fahrzeug anlegen',
    autoLocked: (count: number) =>
      count === 1
        ? '1 Fahrt wurde automatisch festgeschrieben.'
        : `${count} Fahrten wurden automatisch festgeschrieben.`,
    autoLockedNote: 'Korrekturen sind weiterhin möglich, werden aber protokolliert.',
    footerPrivacy:
      'Alle Daten liegen verschlüsselt auf diesem Gerät. Keine Übertragung an einen Server. Adressen werden nur bei aktivierter Entfernungsberechnung an Geoapify (EU) gesendet.',
    footerDisclaimer:
      'Keine Steuerberatung. Elektronische Fahrtenbücher werden in Deutschland nicht zertifiziert; die Anerkennung entscheidet der Betriebsprüfer im Einzelfall.',
    decrypting: 'Daten werden entschlüsselt …',
  },

  welcome: {
    title: 'Fahrtenbuch Pro',
    subtitle: 'Anmelden oder neues Fahrtenbuch anlegen',
    separateNote:
      'Ein neues Konto führt ein eigenes, getrenntes Fahrtenbuch. Fahrten, Rechnungen und Firmendaten werden nicht geteilt.',
    noAccountYet: 'Auf diesem Gerät gibt es noch kein Konto. Legen Sie zuerst eines an.',
    back: 'Zurück',
  },

  setup: {
    stepOf: (step: number, total: number) => `Schritt ${step} von ${total}`,
    languageTitle: 'Sprache wählen',
    languageSubtitle: 'Lässt sich später in den Einstellungen ändern.',
    languageNote:
      'Rechnungen, GoBD-Archiv und Verfahrensdokumentation bleiben immer deutsch — sie gehen an Kunden und an das Finanzamt.',
    continue: 'Weiter',
    accountTitle: 'Fahrtenbuch einrichten',
    accountSubtitle: 'Ihre Daten bleiben auf diesem Gerät. Es gibt keinen Server.',
    name: 'Name',
    password: 'Passwort',
    passwordRepeat: 'Passwort wiederholen',
    passwordHint: 'Mindestens 12 Zeichen. Verschlüsselt alle Daten auf diesem Gerät.',
    passwordStrength: (label: string, issue?: string) =>
      `Stärke: ${label}${issue ? ` — ${issue}` : ''}`,
    mismatch: 'Stimmt nicht überein.',
    nameRequired: 'Bitte einen Namen eintragen.',
    passwordsDiffer: 'Die Passwörter stimmen nicht überein.',
    passwordTooShort: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    deriving: 'Schlüssel wird abgeleitet …',
    createAccount: 'Konto anlegen',
    separateBooks:
      'Jedes Konto führt ein eigenes, getrenntes Fahrtenbuch. Fahrten, Rechnungen und Firmendaten werden nicht geteilt.',
    argonNote:
      'Die Schlüsselableitung dauert bewusst einen Moment (Argon2id, 64 MiB). Das ist der Schutz, falls das Gerät abhandenkommt.',
    createFailed: 'Das Konto konnte nicht angelegt werden.',

    recoveryTitle: 'Wiederherstellungscode',
    recoverySubtitle: 'Einmalig — bitte jetzt notieren.',
    recoveryWarningTitle: 'Ohne diesen Code sind Ihre Daten verloren',
    recoveryWarningBody:
      'Es gibt keinen Server, der Ihr Passwort zurücksetzen könnte. Wenn Sie Passwort und Code verlieren, ist der Zugriff auf alle Fahrten und Rechnungen endgültig weg.',
    copy: 'Kopieren',
    saveAsFile: 'Als Datei speichern',
    acknowledge:
      'Ich habe den Code notiert und an einem sicheren Ort außerhalb dieses Geräts abgelegt.',
    usernameNote: (username: string) => `Ihr Benutzername für die Anmeldung: ${username}`,

    companyTitle: 'Firmendaten',
    companySubtitle: 'Erscheinen auf jeder Rechnung. Lässt sich überspringen.',
    companyNote:
      'Ohne diese Angaben können Sie Fahrten erfassen, aber keine Rechnung stellen — § 14 Abs. 4 UStG verlangt sie.',
    vehicleTitle: 'Erstes Fahrzeug',
    vehicleSubtitle: 'Fahrten und Schichten werden einem Fahrzeug zugeordnet.',
    vehicleNote:
      'Die Ordnungsnummer ist die „Mietwagenkennung“ aus dem BMF-Schreiben vom 11.03.2024 und erscheint in jedem Export.',
    skip: 'Überspringen',
    skipNote: 'Später unter Einstellungen nachtragbar.',
    finish: 'Fertig — zur App',
  },

  login: {
    title: 'Anmelden',
    subtitle: 'Fahrtenbuch Pro',
    greeting: (name: string) => `Willkommen zurück, ${name}.`,
    driver: 'Fahrer',
    username: 'Benutzername',
    password: 'Passwort',
    signIn: 'Anmelden',
    decrypting: 'Entschlüsseln …',
    failed: 'Anmeldung fehlgeschlagen.',
    forgot: 'Passwort vergessen? Mit Wiederherstellungscode zurücksetzen',
    register: 'Neues Konto anlegen',

    recoverTitle: 'Passwort zurücksetzen',
    recoverSubtitle: 'Mit dem Wiederherstellungscode',
    code: 'Wiederherstellungscode',
    codeHint: 'Groß-/Kleinschreibung und Bindestriche egal.',
    newPassword: 'Neues Passwort',
    newPasswordRepeat: 'Neues Passwort wiederholen',
    minChars: 'Mindestens 12 Zeichen.',
    strength: (label: string) => `Stärke: ${label}`,
    reset: 'Passwort zurücksetzen',
    checking: 'Wird geprüft …',
    backToLogin: 'Zurück zur Anmeldung',
    unknownUser: 'Unbekannter Benutzername.',
    resetFailed: 'Zurücksetzen fehlgeschlagen.',
    newCodeTitle: 'Neuer Wiederherstellungscode',
    newCodeSubtitle: 'Der alte Code gilt nicht mehr.',
    notedContinue: 'Notiert — weiter zur App',
    continueToApp: 'Weiter zur App',
  },

  dashboard: {
    greeting: (firstName: string) => `Guten Tag, ${firstName}`,
    tripsThisMonth: 'Fahrten diesen Monat',
    kilometers: 'Kilometer',
    revenueGross: 'Umsatz brutto',
    outstanding: 'Offene Forderungen',
    uninvoiced: (count: number) =>
      count === 1 ? '1 Fahrt noch nicht abgerechnet' : `${count} Fahrten noch nicht abgerechnet`,
    createInvoice: 'Rechnung erstellen',
    uninvoicedBody: (amount: string) => `${amount} aus diesem Monat sind noch offen.`,
    recent: 'Zuletzt erfasst',
    showAll: 'Alle anzeigen',
    noTrips: 'Noch keine Fahrten in diesem Monat',
    firstTrip: 'Erste Fahrt erfassen',
    systemStatus: 'Systemzustand',
    lastBackup: 'Letzte Datensicherung',
    backupLink: 'sichern',
    distanceAuthority: 'Maßgeblich für die Strecke',
    distanceComputed: 'berechnete Entfernung',
    distanceOdometer: 'Tachostand',
    vat: 'Umsatzsteuer',
    vatKleinunternehmer: 'Kleinunternehmer (§ 19 UStG)',
    vatTaxi: 'Taxi — 7 % bis 50 km',
    vatMietwagen: 'Mietwagen — 19 %',
    newTrip: 'Fahrt erfassen',
  },

  trips: {
    title: 'Fahrten',
    description: 'Alle erfassten Fahrten im gewählten Zeitraum.',
    new: 'Fahrt erfassen',
    filterOpen: 'Noch nicht abgerechnet',
    filterInvoiced: 'Abgerechnet',
    searchPlaceholder: 'Name oder Adresse',
    totalTrips: 'Fahrten',
    totalGross: 'brutto',
    empty: 'Keine Fahrten in diesem Zeitraum',
    firstTrip: 'Erste Fahrt erfassen',
    badgeLocked: 'festgeschrieben',
    badgeDistanceFixed: 'Strecke korrigiert',
    badgeInvoiced: 'abgerechnet',
    badgeOpen: 'offen',

    editTitle: 'Fahrt bearbeiten',
    lockedNotice: (date: string) =>
      `Festgeschrieben am ${date}. Änderungen werden protokolliert und im Export ausgewiesen.`,
    sectionRoute: 'Fahrgast und Strecke',
    passenger: 'Fahrgast',
    passengerHint: 'Nachname genügt — der Rest wird ergänzt.',
    fromLabel: 'Von',
    toLabel: 'Nach',
    distance: 'Entfernung (km)',
    distanceOverridden: 'Manuell geändert — wird protokolliert.',
    compute: 'Berechnen',
    computing: 'Berechne …',
    noGeoKey:
      'Automatische Entfernungsberechnung ist nicht eingerichtet. Unter Einstellungen einen Geoapify-Key hinterlegen, oder die Entfernung weiterhin von Hand eintragen.',
    sectionTimes: 'Zeiten und Fahrzeug',
    date: 'Datum',
    vehicle: 'Fahrzeug',
    choose: '— bitte wählen —',
    startTime: 'Fahrtbeginn',
    endTime: 'Fahrtende',
    sectionBilling: 'Abrechnung',
    fareGross: 'Entgelt brutto (€)',
    tip: 'Trinkgeld (€)',
    tipHint: 'Nur eintragen, soweit steuerlich relevant.',
    paymentMethod: 'Zahlungsart',
    tripType: 'Fahrttyp',
    tripTypeHint: 'Pflichtfeld nach BMF 11.03.2024.',
    vatLine: (rate: number, basis: string) => `Umsatzsteuer ${rate} % · ${basis}`,
    vatOf: (amount: string) => ` · davon ${amount} USt`,
    sectionOrder: 'Auftrag und Notizen',
    linkedOrder: 'Zugehöriger Auftragseingang',
    linkedOrderHint:
      'Verknüpft die Fahrt mit der Aufzeichnung nach § 49 Abs. 4 Satz 4 PBefG.',
    noOrder: '— kein Auftrag verknüpft —',
    comment: 'Kommentar',
    commentPlaceholder: 'z. B. Wartezeit, Sonderwünsche, Zwischenstopp',
    amendmentReason: 'Begründung der Korrektur',
    amendmentHint: 'Wird in der Protokollkette und in jedem Export ausgewiesen.',
    amendmentPlaceholder: 'z. B. Zahlendreher bei der Entfernung',
    saveAndNext: 'Speichern und nächste Fahrt',
    errPassenger: 'Bitte den Fahrgast eintragen.',
    errFrom: 'Bitte die Startadresse eintragen.',
    errTo: 'Bitte die Zieladresse eintragen.',
    errVehicle: 'Bitte ein Fahrzeug auswählen. Fahrzeuge unter Einstellungen anlegen.',
    errDistance: 'Bitte eine Entfernung eintragen.',
    errAmendment:
      'Diese Fahrt ist festgeschrieben. Bitte eine Begründung für die Korrektur angeben.',
    errNoGeoKey: 'Kein Geoapify-Key hinterlegt — bitte Entfernung eintragen.',
    errNotLocated: 'Adressen konnten nicht verortet werden. Bitte Entfernung eintragen.',
    computed: (km: string, minutes: number) =>
      `Berechnet: ${km} km, ca. ${minutes} Minuten Fahrzeit.`,
    computeFailed: 'Entfernung konnte nicht berechnet werden.',
    addManually: 'Bitte Entfernung manuell eintragen.',
    habitual: 'üblich',
    tripCount: (count: number) => `${count} Fahrten`,
    notFound: 'Fahrt nicht gefunden.',

    detailTitle: (date: string) => `Fahrt vom ${date}`,
    edit: 'Bearbeiten',
    lockedOn: (date: string) => `festgeschrieben am ${date}`,
    distanceFixed: 'Strecke manuell korrigiert',
    sectionTrip: 'Fahrt',
    times: 'Zeiten',
    route: 'Strecke',
    computedWas: (km: string) => `(berechnet: ${km} km)`,
    net: 'Netto',
    vatOfWhich: 'davon Umsatzsteuer',
    orderNumber: 'Auftrag lfd. Nr.',
    orderReceived: 'Auftragseingang',
    orderChannel: 'Kanal',
    orderAccepted: 'Auftrag übernommen',
    orderNotAccepted: 'nicht erfasst',
    changeLog: 'Änderungsprotokoll',
    changeLogNote:
      'Vollständige, unveränderbare Historie dieses Datensatzes. Jeder Eintrag ist per SHA-256 mit seinem Vorgänger verkettet; die Kette wird bei jedem Export geprüft und mitgeliefert.',
  },

  orders: {
    title: 'Auftragseingang',
    description: 'Gesetzlich vorgeschriebene Aufzeichnung nach § 49 Abs. 4 Satz 4 PBefG.',
    new: 'Auftrag erfassen',
    intro:
      'Der Eingang jedes Beförderungsauftrags ist zu erfassen und mindestens ein Jahr aufzubewahren; steuerlich sind es sechs Jahre. Erfassen Sie den Auftrag, sobald der Anruf kommt — nicht erst nach der Fahrt.',
    gapTitle: 'Lücke in der Nummernfolge',
    gapBody: (numbers: string) =>
      `Im laufenden Jahr fehlen die laufenden Nummern ${numbers}. Eine Lücke im Auftragseingangsbuch wird als unterdrückter Auftrag gelesen.`,
    empty: 'Noch keine Aufträge erfasst',
    number: (n: number) => `Nr. ${n}`,
    noName: 'Ohne Namen',
    received: (when: string) => `Eingang ${when}`,
    acceptedAs: (short: string, label: string) => ` · übernommen ${short} = ${label}`,
    requested: (when: string) => ` · gewünscht ${when}`,
    linkedTrip: 'Fahrt verknüpft',
    logTrip: 'Fahrt erfassen',
    formTitle: (next: number | null) =>
      next === null ? 'Neuer Auftragseingang' : `Neuer Auftragseingang — lfd. Nr. ${next}`,
    groupIntake: 'Eingang',
    receivedAt: 'Eingang am',
    receivedAtHint: 'Zeitpunkt der Bestellung, nicht der Fahrt.',
    channel: 'Kanal',
    groupOrderer: 'Besteller',
    ordererName: 'Name',
    ordererAddress: 'Anschrift',
    ordererPhone: 'Telefon',
    ordererEmail: 'E-Mail',
    groupRide: 'Fahrt',
    pickup: 'Abholstelle',
    pickupHint: 'Straße und Hausnummer.',
    destination: 'Zielort',
    destinationHint: 'Ort und Straße, soweit bei der Bestellung bekannt.',
    requestedPickup: 'Gewünschte Abholung',
    groupAcceptance: 'Beförderungsauftrag übernommen',
    acceptedToggle: 'Auftrag wurde bereits übernommen',
    acceptancePlace: 'Ort der Übernahme',
    acceptancePlaceHint: '§ 49 Abs. 4 PBefG lässt nur diese drei Fälle zu.',
    acceptedAt: 'Übernommen am',
    vehicle: 'Fahrzeug',
    vehicleNone: '— noch nicht disponiert —',
    driver: 'Fahrer',
    acceptanceLater:
      'Die Übernahme kann später nachgetragen werden. Ohne sie lässt sich nicht belegen, dass der Auftrag am Betriebssitz, in der Wohnung oder während der Fahrt fernmündlich angenommen wurde.',
    notes: 'Bemerkung',
    pickupRequired: 'Die Abholstelle ist eine Pflichtangabe.',
    findings: {
      pickupLocation: 'Abholstelle fehlt — Pflichtangabe im Auftragseingangsbuch.',
      acceptancePlace:
        'Übernahme des Beförderungsauftrags nicht erfasst. § 49 Abs. 4 PBefG lässt nur Betriebssitz, Wohnung oder fernmündlichen Eingang während der Fahrt zu.',
      acceptedAt:
        'Zeitpunkt der Auftragsübernahme fehlt — Pflichtangabe im Auftragseingangsbuch.',
      vehicleId: 'Kein Fahrzeug zur Übernahme vermerkt — Pflichtangabe im Auftragseingangsbuch.',
      ordererName: 'Besteller nicht benannt.',
    },
  },

  shifts: {
    title: 'Schichten',
    description: 'Kilometerstand, Einnahmen und Abgleich gegen die erfassten Fahrten.',
    startToday: 'Schicht für heute anlegen',
    empty: 'Keine Schichten in diesem Zeitraum',
    emptyBody:
      'Eine Schicht entsteht automatisch, sobald die erste Fahrt eines Tages erfasst wird.',
    noVehicle: 'Kein Fahrzeug',
    tripCount: (count: number) => (count === 1 ? '1 Fahrt' : `${count} Fahrten`),
    closed: 'abgeschlossen',
    open: 'offen',
    odometerMissing:
      'Der Kilometerstand bei Schichtbeginn fehlt. Er ist Pflichtfeld nach BMF 11.03.2024 Abschnitt III.',
    workStart: 'Arbeitsanfang',
    workEnd: 'Arbeitsende',
    vehicle: 'Fahrzeug',
    odometerStart: 'Tacho Arbeitsanfang (km)',
    odometerEnd: 'Tacho Arbeitsende (km)',
    breaks: 'Pausen',
    addBreak: 'Pause hinzufügen',
    noBreaks: 'Keine Pause erfasst.',
    breakFrom: (n: number) => `Pause ${n} von`,
    breakTo: (n: number) => `Pause ${n} bis`,
    until: 'bis',
    remove: 'entfernen',
    reconciliation: 'Abgleich',
    sumTrips: 'Summe Fahrten',
    perOdometer: 'Laut Tacho',
    difference: 'Differenz',
    revenue: 'Einnahmen',
    workingTime: 'Arbeitszeit netto',
    tripsLonger:
      'Die erfassten Fahrten sind länger als die Tachodifferenz. Bitte die Strecken prüfen — das ist der Punkt, an dem eine Prüfung ansetzt.',
    odometerLonger:
      'Die Tachodifferenz übersteigt die erfassten Fahrten. Das ist bei Leerfahrten und dem Weg nach Hause normal, sollte aber erklärbar sein.',
    byPaymentMethod: 'Nach Zahlungsart:',
    tipTotal: (amount: string) => ` · Trinkgeld ${amount}`,
    confirmedBy: (name: string, date: string) =>
      `Bestätigt von ${name} am ${date}. Entspricht der Unterschrift des Fahrers auf dem Fahrtennachweis.`,
    close: 'Schicht abschließen und bestätigen',
    errOdometerEnd: 'Bitte zuerst den Kilometerstand bei Schichtende eintragen.',
    closeReason: (name: string) => `Schichtabschluss durch ${name}`,
  },

  invoices: {
    title: 'Rechnungen',
    outstanding: (amount: string) => `${amount} offen`,
    create: 'Rechnung erstellen',
    gapTitle: 'Lücke im Rechnungsnummernkreis',
    gapEntry: (circle: string, missing: string) => `${circle}: es fehlen ${missing}`,
    gapNote:
      '. Eine Lücke wird in der Betriebsprüfung als unterdrückte Rechnung gewertet (§ 14 Abs. 4 Nr. 4 UStG). Klären Sie den Befund.',
    empty: 'Keine Rechnungen',
    firstInvoice: 'Erste Rechnung erstellen',
    draft: 'Entwurf',
    noCustomer: 'Ohne Kunde',
    positions: (count: number) => (count === 1 ? '1 Position' : `${count} Positionen`),
    overdueBadge: 'überfällig',
    dunningBadge: (level: number) => `Mahnstufe ${level}`,
    eInvoice: 'E-Rechnung',
    dueOn: (date: string) => `fällig ${date}`,
    kleinbetrag: 'Kleinbetrag',

    formTitleNew: 'Rechnung erstellen',
    formTitleEdit: 'Rechnungsentwurf bearbeiten',
    formDescription: 'Eine Rechnungsnummer wird erst beim Ausstellen vergeben.',
    period: 'Zeitraum',
    uninvoicedTrips: (count: number) => `Noch nicht abgerechnete Fahrten (${count})`,
    selectAll: 'Alle auswählen',
    clearSelection: 'Auswahl aufheben',
    noOpenTrips: 'Keine offenen Fahrten im gewählten Zeitraum.',
    selectTrips: 'Fahrten auswählen',
    takeTrips: (count: number) =>
      count === 1 ? '1 Fahrt übernehmen' : `${count} Fahrten übernehmen`,
    errSelectTrip: 'Bitte mindestens eine Fahrt auswählen.',
    recipient: 'Empfänger',
    customerName: 'Name',
    customerAddress: 'Anschrift',
    customerAddressHint: 'Straße, PLZ und Ort — je Zeile.',
    customerVatId: 'USt-IdNr. des Kunden',
    positionsTitle: 'Positionen',
    netPerUnit: (rate: number) => `netto · ${rate} % USt`,
    netTotal: 'Netto',
    vatTotal: 'Umsatzsteuer',
    grandTotal: 'Gesamt',
    kleinbetragBadge: 'Kleinbetragsrechnung § 33 UStDV — Kurzform',
    kleinunternehmerBadge: '§ 19 UStG — ohne Umsatzsteuer',
    mietwagenBadge: 'Mietwagen § 49 PBefG — 19 %',
    datesAndText: 'Termine und Text',
    issueDate: 'Rechnungsdatum',
    dueDate: 'Fällig am',
    serviceFrom: 'Leistung von',
    serviceTo: 'Leistung bis',
    paymentTerms: 'Zahlungsbedingungen',
    notes: 'Bemerkung',
    eInvoiceField: 'E-Rechnung',
    eInvoiceNone: 'Nur PDF',
    eInvoiceFacturx: 'Factur-X / ZUGFeRD (PDF mit eingebettetem XML)',
    problemsTitle: 'Pflichtangaben fehlen',
    issue: 'Rechnung stellen',
    issuing: 'Wird gestellt …',
    saveDraft: 'Als Entwurf speichern',
    issueFailed: 'Ausstellen fehlgeschlagen.',

    detailDraftTitle: 'Rechnungsentwurf',
    notFound: 'Rechnung nicht gefunden',
    backToList: 'Zurück zur Übersicht',
    kleinbetragFull: 'Kleinbetragsrechnung § 33 UStDV',
    stornoBadge: 'Stornorechnung',
    overdueTitle: 'Zahlungsziel überschritten',
    overdueBody: (date: string) => `Fällig war der ${date}.`,
    lastDunned: (when: string) => `Zuletzt gemahnt am ${when}.`,
    neverDunned: 'Bisher wurde nicht gemahnt.',
    position: (n: number) => `Pos. ${n}`,
    withoutVat: 'ohne USt',
    withVat: (rate: number) => `${rate} % USt`,
    netAmount: 'Nettobetrag',
    vatOn: (rate: number, base: string) => `${rate} % USt auf ${base}`,
    invoiceTotal: 'Rechnungsbetrag',
    details: 'Angaben',
    invoiceNumber: 'Rechnungsnummer',
    notAssigned: '(noch nicht vergeben)',
    numberCircle: 'Nummernkreis',
    servicePeriod: 'Leistungszeitraum',
    recipientTerm: 'Empfänger',
    addressTerm: 'Anschrift',
    customerVatTerm: 'USt-IdNr. Kunde',
    paidTerm: 'Bezahlt',
    documents: 'Dokumente',
    invoiceAsPdf: 'Rechnung als PDF',
    generating: 'Wird erzeugt …',
    dunningAsPdf: (label: string) => `${label} als PDF`,
    eInvoiceNote:
      'Empfangen müssen Sie E-Rechnungen bereits heute. Ausstellen wird für inländische B2B-Umsätze erst zum 01.01.2028 verpflichtend — bei über 800.000 € Vorjahresumsatz zum 01.01.2027. Kleinunternehmer sind nach § 34a UStDV dauerhaft befreit.',
    eInvoiceNotReady: 'Noch nicht bereit für die E-Rechnung',
    eInvoiceGenerated: (format: string) => `${format} wurde erzeugt und heruntergeladen.`,
    eInvoiceReason: (format: string) => `E-Rechnung erzeugt: ${format}`,
    eInvoiceLast: (when: string) => `Zuletzt erzeugt am ${when}.`,
    draftTitle: 'Entwurf',
    draftBody:
      'Dieser Entwurf hat noch keine Rechnungsnummer. Erst beim Stellen wird eine vergeben, damit ein verworfener Entwurf keine Lücke im Nummernkreis hinterlässt.',
    editDraft: 'Entwurf bearbeiten',
    paymentAndDunning: 'Zahlung und Mahnwesen',
    paidOn: (when: string) => `Als bezahlt erfasst am ${when}.`,
    paidNoDate: 'Als bezahlt erfasst.',
    undo: 'Zurücknehmen',
    paymentReceived: 'Zahlungseingang',
    markPaid: 'Als bezahlt erfassen',
    recording: 'Wird erfasst …',
    creating: 'Wird erstellt …',
    createDunning: (label: string) => `${label} erstellen`,
    cancelReason: 'Stornogrund',
    cancelReasonHint: 'Der Grund steht auf der Stornorechnung und im Protokoll.',
    createStorno: 'Stornorechnung erstellen',
    cancelling: 'Wird storniert …',
    cancelNote:
      'Eine gestellte Rechnung wird nicht geändert. Ist sie falsch, erzeugt das Storno ein eigenes Dokument mit eigener Nummer; die ursprüngliche Nummer bleibt vergeben, damit der Nummernkreis lückenlos bleibt.',
    cancelInvoice: 'Rechnung stornieren',
    errCancelReason: 'Bitte einen Stornogrund angeben.',
    errAmount: 'Betrag konnte nicht gelesen werden.',
    errGeneric: 'Der Vorgang ist fehlgeschlagen.',
    history: 'Änderungshistorie',
    problems: {
      companyName: 'Vollständiger Name des leistenden Unternehmers fehlt.',
      companyAddress: 'Vollständige Anschrift des leistenden Unternehmers fehlt.',
      taxNumber: 'Steuernummer oder Umsatzsteuer-Identifikationsnummer fehlt.',
      noLines: 'Die Rechnung enthält keine Positionen.',
      lineDescription: 'Eine Position hat keine Leistungsbeschreibung.',
      serviceDate: 'Zeitpunkt der Leistung fehlt.',
      customerName: 'Vollständiger Name des Leistungsempfängers fehlt.',
      customerAddress: 'Vollständige Anschrift des Leistungsempfängers fehlt.',
    },
  },

  exports: {
    title: 'Export',
    description:
      'Zeitraum wählen, Format wählen. Die Änderungshistorie ist in jedem Export enthalten.',
    period: 'Zeitraum',
    selection: 'Auswahl',
    month: 'Monat',
    year: 'Jahr',
    custom: 'Freier Zeitraum',
    check: 'Zeitraum prüfen',
    checking: 'Wird geprüft …',
    previewTitle: (period: string) => `Vorschau — ${period}`,
    trips: 'Fahrten',
    kilometers: 'Kilometer',
    gross: 'Brutto',
    vat: 'Umsatzsteuer',
    chainOk: (count: number) => `Protokollkette geprüft — ${count} Einträge`,
    chainBad: (count: number) => `Protokollkette beanstandet — ${count} Befunde`,
    odometerDeviation: 'Tachoabweichung in mindestens einer Schicht',
    odometerMissing: 'Kilometerstand fehlt in mindestens einer Schicht',
    chainBrokenTitle: 'Die Protokollkette ist nicht intakt',
    chainBrokenBody:
      'Exportieren Sie trotzdem — das Prüfprotokoll ist Teil des Archivs — und klären Sie den Befund. Eine gebrochene Kette bedeutet, dass die Datenbank außerhalb der App verändert wurde.',
    pdfDescription:
      'Lesbare Fassung für Menschen — mit Änderungshistorie an der jeweiligen Fahrt und Tachoabgleich.',
    pdfNote: 'Für die Übergabe an das Finanzamt als Anlage.',
    pdfAction: 'PDF erstellen',
    xlsxTitle: 'Excel',
    xlsxDescription: 'Fahrten, Schichten, Aufträge, Rechnungen und Protokoll als Arbeitsmappe.',
    xlsxNote: 'Für eigene Auswertungen und die Steuerkanzlei.',
    xlsxAction: 'Excel erstellen',
    gobdTitle: 'GoBD-Archiv (Z3)',
    gobdDescription:
      'Strukturierte CSV-Dateien mit index.xml, Prüfprotokoll und vollständiger Protokollkette.',
    gobdNote: 'Das ist der Export für die Betriebsprüfung.',
    gobdAction: 'Archiv erstellen',
    creating: 'Wird erstellt …',
    whyThreeTitle: 'Warum drei Formate',
    whyThreeBody:
      'GoBD Rz. 129 nennt die Umwandlung von Grundaufzeichnungen in ein PDF-Format ausdrücklich als Beispiel einer unzulässigen Beeinträchtigung der maschinellen Auswertbarkeit; Rz. 144 schließt Druckdateien als Archivformat aus. Für einen Datenzugriff nach § 147 Abs. 6 AO ist daher das GoBD-Archiv zu übergeben. PDF und Excel sind für Menschen.',
    verfahrenTitle: 'Verfahrensdokumentation',
    verfahrenBody:
      'GoBD Rz. 151–154 verlangt eine Verfahrensdokumentation, aus der Inhalt, Aufbau, Ablauf und Ergebnisse des DV-Verfahrens vollständig und nachvollziehbar hervorgehen. Ohne sie kann ein Prüfer die formelle Ordnungsmäßigkeit beanstanden. Die Vorlage ist mit Ihren Einstellungen vorbefüllt und muss um Ihre betrieblichen Abläufe ergänzt werden.',
    verfahrenDownload: 'Vorlage herunterladen',
    exportFailed: 'Export fehlgeschlagen.',
    previewFailed: 'Vorschau fehlgeschlagen.',
  },

  backup: {
    title: 'Datensicherung',
    description: 'Ihre Aufzeichnungen liegen ausschließlich auf diesem Gerät.',
    whyTitle: 'Warum das erzwungen wird',
    why1:
      'Fahrtaufzeichnungen sind zehn Jahre aufzubewahren (§ 147 Abs. 1 Nr. 1 i. V. m. Abs. 3 AO). GoBD Rz. 103–104 macht die Sicherung gegen Verlust zur Pflicht des Steuerpflichtigen: können die Daten nicht mehr vorgelegt werden, ist die Buchführung formell nicht ordnungsmäßig — mit der Folge einer Schätzung nach § 162 AO.',
    why2: (warnDays: number, blockDays: number) =>
      `Hinzu kommt eine technische Eigenheit: iOS kann den Speicher einer Web-App unter Speicherdruck räumen, auch wenn sie auf dem Home-Bildschirm liegt. Deshalb erinnert die App nach ${warnDays} Tagen und sperrt den Monatsabschluss nach ${blockDays} Tagen.`,
    why3:
      'Bewahren Sie die Sicherung in Deutschland oder der EU auf. § 146 Abs. 2 AO verlangt, Bücher und Aufzeichnungen im Geltungsbereich des Gesetzes zu führen und aufzubewahren; Abs. 2a lässt die EU ohne Genehmigung zu, für Drittstaaten wäre eine Bewilligung nötig.',
    storageTitle: 'Speicher auf diesem Gerät',
    lastBackup: 'Letzte Sicherung',
    usedStorage: 'Belegter Speicher',
    usedOf: (used: string, quota: string, percent: string) => `${used} von ${quota} (${percent} %)`,
    notAvailable: 'nicht ermittelbar',
    protected: 'Vor Verdrängung geschützt',
    unknown: 'unbekannt',
    yes: 'ja',
    no: 'nein',
    requestPersistent: 'Dauerhaften Speicher anfordern',
    createTitle: 'Sicherung erstellen',
    createBody:
      'Sie erhalten zwei Dateien: eine verschlüsselte Sicherungsdatei zum Wiederherstellen und das GoBD-Gesamtarchiv als lesbares CSV-Paket. Die Passphrase ist unabhängig von Ihrem Anmeldepasswort — notieren Sie sie, sie lässt sich nicht zurücksetzen.',
    passphrase: 'Passphrase',
    passphraseHint: 'Mindestens 12 Zeichen.',
    passphraseRepeat: 'Passphrase wiederholen',
    label: 'Bezeichnung',
    labelHint: 'Erscheint im Kopf der Datei, hilft beim Auseinanderhalten.',
    labelPlaceholder: 'z. B. Monatssicherung August',
    create: 'Sicherung erstellen',
    creating: 'Wird erstellt …',
    errShort: 'Die Passphrase muss mindestens 12 Zeichen lang sein.',
    errMismatch: 'Die Passphrasen stimmen nicht überein.',
    failed: 'Sicherung fehlgeschlagen.',
    done: (trips: number, invoices: number, entries: number) =>
      `Sicherung erstellt: ${trips} Fahrten, ${invoices} Rechnungen, ${entries} Protokolleinträge. Zusätzlich wurde das GoBD-Gesamtarchiv heruntergeladen.`,

    restoreTitle: 'Sicherung wiederherstellen',
    restoreWarning:
      'Die Wiederherstellung ersetzt alle Daten auf diesem Gerät. Ein Zusammenführen ist nicht möglich, weil die Protokollkette sonst weder der Sicherung noch dem Gerät entspräche und damit nicht mehr prüfbar wäre.',
    file: 'Sicherungsdatei',
    createdAt: 'Erstellt am',
    contents: 'Inhalt',
    contentsValue: (trips: number, shifts: number, invoices: number, entries: number) =>
      `${trips} Fahrten · ${shifts} Schichten · ${invoices} Rechnungen · ${entries} Protokolleinträge`,
    chainAtBackup: 'Protokollkette bei Sicherung',
    chainOk: 'unverändert',
    chainBad: 'beanstandet',
    filePassphrase: 'Passphrase der Sicherung',
    confirmation: 'Bestätigung',
    confirmationHint: 'Tippen Sie WIEDERHERSTELLEN, um fortzufahren.',
    confirmationWord: 'WIEDERHERSTELLEN',
    errConfirmation: 'Bitte WIEDERHERSTELLEN eintippen, um den Vorgang zu bestätigen.',
    replaceAll: 'Alle Daten ersetzen',
    restoring: 'Wird wiederhergestellt …',
    restoreFailed: 'Wiederherstellung fehlgeschlagen.',
    fileUnreadable: 'Datei konnte nicht gelesen werden.',
    restored: (trips: number, invoices: number, entries: number, chain: string) =>
      `Wiederhergestellt: ${trips} Fahrten, ${invoices} Rechnungen, ${entries} Protokolleinträge. Protokollkette nach der Wiederherstellung: ${chain}.`,
    chainUnchanged: 'unverändert',
    chainProblems: (count: number) => `${count} Beanstandungen`,
  },

  settings: {
    title: 'Einstellungen',
    description: 'Diese Angaben erscheinen auf jeder Rechnung und in jedem Export.',
    tabCompany: 'Firma',
    tabInvoice: 'Rechnungslayout',
    tabApp: 'App & Fahrer',
    tabVehicles: 'Fahrzeuge',
    saved: 'Gespeichert.',
    unsaved: 'Es gibt ungespeicherte Änderungen.',

    companyCard: 'Unternehmen',
    companyName: 'Firmenname',
    ownerName: 'Inhaber / Geschäftsführung',
    street: 'Straße und Hausnummer',
    postalCode: 'PLZ',
    city: 'Ort',
    phone: 'Telefon',
    email: 'E-Mail',
    emailHint: 'Elektronische Adresse BT-34 der E-Rechnung.',
    taxCard: 'Steuer und Konzession',
    taxNumber: 'Steuernummer',
    taxNumberHint: 'Eine von beiden ist Pflicht: § 14 Abs. 4 Nr. 2 UStG.',
    vatId: 'USt-IdNr.',
    registrationNumber: 'Handelsregisternummer',
    registrationHint: 'BT-30. Nur für die E-Rechnung nötig, falls keine USt-IdNr. vorliegt.',
    licenceType: 'Konzessionsart',
    licenceMietwagen: 'Mietwagen (§ 49 PBefG)',
    licenceTaxi: 'Taxi (§ 47 PBefG)',
    kleinunternehmer: 'Kleinunternehmer',
    kleinunternehmerNo: 'Nein — Umsatzsteuer wird ausgewiesen',
    kleinunternehmerYes: 'Ja — § 19 UStG, keine Umsatzsteuer',
    taxiNote:
      'Als Taxiunternehmen gilt der ermäßigte Satz von 7 % für Fahrten innerhalb einer Gemeinde oder bis 50 km Beförderungsstrecke (§ 12 Abs. 2 Nr. 10 Buchst. b UStG). Darüber greift der Regelsatz.',
    mietwagenNote:
      'Mietwagenverkehr nach § 49 PBefG ist nicht begünstigt: es gelten immer 19 %, unabhängig von der Entfernung. Der ermäßigte Satz ist Taxen nach § 47 PBefG vorbehalten — vom BFH mehrfach bestätigt und vom BVerfG nicht beanstandet.',
    bankCard: 'Bankverbindung',
    bankName: 'Bank',
    iban: 'IBAN',
    bic: 'BIC',

    invoiceCard: 'Rechnungslayout',
    logo: 'Logo',
    logoHint: 'PNG oder JPEG, erscheint oben rechts.',
    logoTypeError: 'Nur PNG oder JPEG.',
    logoSizeError: 'Das Logo sollte unter 500 kB liegen — es steckt in jeder Rechnung.',
    accentColor: 'Akzentfarbe',
    paymentTerms: 'Zahlungsbedingungen',
    paymentTermsHint: 'Standardtext auf jeder neuen Rechnung.',
    footerNote: 'Fußnote',
    footerNoteHint: 'Etwa ein Hinweis auf die Steuerschuldnerschaft oder das Handelsregister.',

    driverCard: 'Fahrer und Sprache',
    displayName: 'Name des Fahrers',
    driverCode: 'Fahrerkennung',
    driverCodeHint: 'Pflichtfeld nach BMF 11.03.2024.',
    language: 'Sprache der Oberfläche',
    languageHint:
      'Rechnungen, GoBD-Archiv und Verfahrensdokumentation bleiben deutsch — sie gehen an Kunden und an das Finanzamt.',
    driverSaved: 'Fahrerdaten gespeichert.',
    driverNameRequired: 'Bitte einen Namen eintragen.',
    saveDriver: 'Fahrerdaten speichern',

    distanceCard: 'Entfernungsermittlung',
    distanceAuthority: 'Maßgeblicher Wert',
    distanceComputed: 'Berechnete Fahrstrecke (Geoapify)',
    distanceOdometer: 'Tachometer — manuell erfasst',
    kassenSichVTitle: 'Offene Rechtsfrage — bitte mit dem Steuerberater klären',
    kassenSichVBody:
      'Seit dem 01.02.2026 erfasst § 1 Abs. 2 Satz 2 KassenSichV auch app-gestützte Systeme, „sofern diese die Funktion eines EU-Taxameters oder Wegstreckenzählers übernehmen“. Eine App, die die Strecke selbst ermittelt, ist plausibel erfasst — dann wären eine zertifizierte TSE und eine Konformitätsbewertung nach Eichrecht erforderlich. Rechtsprechung oder Verwaltungsanweisung dazu gibt es noch nicht. Die Umstellung auf „Tachometer“ verlagert den maßgeblichen Wert ohne Datenmigration.',
    geoCard: 'Adresssuche',
    geoKey: 'Geoapify API-Schlüssel',
    geoKeyPlaceholder: 'ohne Schlüssel: nur gelernte Adressen',
    geoLat: 'Suchschwerpunkt Breite',
    geoLon: 'Länge',
    geoCountry: 'Land',
    lockCard: 'Festschreibung und Abgleich',
    lockDays: 'Festschreibung nach Tagen',
    lockDaysHint: 'Der Safe Harbour aus BMF 03.03.2022 Rn. 29 liegt bei 7 Tagen.',
    tolerance: 'Toleranz Tachoabgleich (km)',
    toleranceHint:
      'Ab dieser Abweichung zwischen Fahrten und Tachodifferenz warnt die Schicht.',
    backupCard: 'Datensicherung',
    warnAfter: 'Warnung nach Tagen',
    blockAfter: 'Monatsabschluss sperren nach Tagen',
    lastBackupAt: (when: string) => `Letzte Sicherung: ${when}.`,
    neverBackedUp: 'Es wurde noch nie gesichert.',

    vehiclesCard: 'Fahrzeuge',
    vehiclesNote:
      'Die Ordnungsnummer ist die „Mietwagenkennung“ aus dem BMF-Schreiben vom 11.03.2024 und erscheint in jedem Export.',
    noVehicles: 'Noch kein Fahrzeug angelegt.',
    vehicleNumber: (code: string) => ` · Nr. ${code}`,
    deactivate: 'Stilllegen',
    reactivate: 'Reaktivieren',
    addVehicle: 'Fahrzeug hinzufügen',
    licensePlate: 'Kennzeichen',
    vehicleCode: 'Ordnungsnummer',
    vehicleCodeHint: 'Leer lassen für die nächste freie Nummer.',
    make: 'Marke',
    model: 'Modell',
    createVehicle: 'Fahrzeug anlegen',
    creatingVehicle: 'Wird angelegt …',
    errPlate: 'Das Kennzeichen fehlt.',
    createFailed: 'Anlegen fehlgeschlagen.',
  },

  audit: {
    loading: 'Protokoll wird geladen …',
    empty: 'Keine Einträge.',
    reason: 'Begründung:',
    actions: {
      create: 'Angelegt',
      update: 'Geändert',
      delete: 'Gelöscht',
      lock: 'Festgeschrieben',
      unlock: 'Festschreibung aufgehoben',
      'invoice.issue': 'Rechnung gestellt',
      'invoice.cancel': 'Storniert',
      'invoice.pay': 'Zahlung erfasst',
      'invoice.dun': 'Mahnung erstellt',
      'shift.close': 'Schicht abgeschlossen',
      export: 'Exportiert',
      backup: 'Gesichert',
      restore: 'Wiederhergestellt',
      login: 'Angemeldet',
      'account.create': 'Konto angelegt',
    } as Record<string, string>,
    /**
     * Looked up by the stored `field` key. Entries written before a language
     * change keep their own German `label`, which is used as the fallback —
     * the stored text is inside the hash preimage and cannot be rewritten.
     */
    fields: {
      date: 'Datum',
      startedAt: 'Fahrtbeginn',
      endedAt: 'Fahrtende',
      passengerId: 'Fahrgast',
      fromAddressId: 'Von',
      toAddressId: 'Nach',
      tripType: 'Fahrttyp',
      distanceMeters: 'Strecke',
      computedDistanceMeters: 'Berechnete Strecke',
      distanceOverridden: 'Strecke manuell geändert',
      distanceSource: 'Quelle der Strecke',
      fareGrossCents: 'Entgelt brutto',
      vatRatePercent: 'Umsatzsteuersatz',
      paymentMethod: 'Zahlungsart',
      tipCents: 'Trinkgeld',
      comment: 'Kommentar',
      orderId: 'Auftrag',
      invoiceId: 'Rechnung',
      shiftId: 'Schicht',
      vehicleId: 'Fahrzeug',
      driverId: 'Fahrer',
      lockedAt: 'Festgeschrieben am',
      odometerStartMeters: 'Kilometerstand Beginn',
      odometerEndMeters: 'Kilometerstand Ende',
      breaks: 'Pausen',
      signedOffBy: 'Unterschrift Fahrer',
      closedAt: 'Schicht abgeschlossen',
      sequenceNumber: 'Laufende Nummer',
      receivedAt: 'Auftragseingang',
      channel: 'Bestellkanal',
      ordererName: 'Besteller',
      ordererAddress: 'Anschrift Besteller',
      ordererPhone: 'Telefon Besteller',
      ordererEmail: 'E-Mail Besteller',
      pickupLocation: 'Abholstelle',
      destination: 'Zielort',
      acceptedAt: 'Auftrag übernommen am',
      acceptancePlace: 'Ort der Auftragsübernahme',
      requestedPickupAt: 'Gewünschte Abholung',
      invoiceNumber: 'Rechnungsnummer',
      status: 'Status',
      issueDate: 'Rechnungsdatum',
      dueDate: 'Fällig am',
      customerName: 'Kunde',
      customerAddress: 'Kundenanschrift',
      netTotalCents: 'Nettobetrag',
      vatTotalCents: 'Umsatzsteuer',
      grossTotalCents: 'Bruttobetrag',
      paidAt: 'Bezahlt am',
      dunningLevel: 'Mahnstufe',
      lastName: 'Nachname',
      firstName: 'Vorname',
      company: 'Firma',
      phone: 'Telefon',
      email: 'E-Mail',
      label: 'Bezeichnung',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      postalCode: 'PLZ',
      city: 'Ort',
      notes: 'Notizen',
      displayName: 'Name des Fahrers',
      driverCode: 'Fahrerkennung',
    } as Record<string, string>,
    /** Booleans are stored as these words; mapped back for display. */
    booleanTrue: 'ja',
    booleanFalse: 'nein',
  },

  labels: {
    payment: {
      bar: 'Bar',
      ec: 'EC-Karte',
      kreditkarte: 'Kreditkarte',
      rechnung: 'Auf Rechnung',
      app: 'App / Vermittler',
      sonstiges: 'Sonstiges',
    },
    tripType: {
      tariffahrt: 'Tariffahrt',
      sonstige: 'Sonstige Fahrt',
    },
    distanceSource: {
      geoapify: 'automatisch berechnet (Geoapify)',
      manual: 'manuell eingetragen',
      odometer: 'Tachostand',
    },
    channel: {
      telefon: 'Telefon',
      email: 'E-Mail / schriftlich',
      funk: 'Funk',
      app: 'App',
      vermittler: 'Vermittler',
      persoenlich: 'Mündlich / persönlich',
      stammkunde: 'Stammkunde',
      sonstiges: 'Sonstiges',
    },
    acceptance: {
      betriebssitz: 'Betriebssitz',
      wohnung: 'Wohnung des Unternehmers',
      funk: 'Während der Fahrt (fernmündlich)',
    },
    invoiceStatus: {
      entwurf: 'Entwurf',
      offen: 'Offen',
      bezahlt: 'Bezahlt',
      storniert: 'Storniert',
      ueberfaellig: 'Überfällig',
    },
    dunning: {
      0: 'Keine Mahnung',
      1: 'Zahlungserinnerung',
      2: '1. Mahnung',
      3: '2. Mahnung',
    },
    eInvoice: {
      keine: 'Keine E-Rechnung',
      facturx: 'Factur-X / ZUGFeRD (EN 16931)',
      'xrechnung-ubl': 'XRechnung 3.0.2 (UBL)',
      'xrechnung-cii': 'XRechnung 3.0.2 (CII)',
    },
  },

  backupStatus: {
    never:
      'Es wurde noch nie eine Datensicherung erstellt. Ihre Aufzeichnungen existieren derzeit nur auf diesem Gerät.',
    overdue: (days: number) =>
      `Letzte Sicherung vor ${days} Tagen. Der Monatsabschluss ist gesperrt, bis eine aktuelle Sicherung vorliegt.`,
    due: (days: number) => `Letzte Sicherung vor ${days} Tagen. Bitte jetzt sichern.`,
    today: 'Heute gesichert.',
    ok: (days: number) => `Zuletzt vor ${days} Tagen gesichert.`,
    lastNever: 'nie',
    lastToday: 'heute',
    lastYesterday: 'gestern',
    lastDaysAgo: (days: number) => `vor ${days} Tagen`,
  },
};

export type Dictionary = typeof de;
